/* Hooks */
import useMst from '~/src/hooks/useMst';

/* Constants */
import { VALIDATION_TYPES } from '~/src/utils/validation';
import { LAYOUT_MODAL_TYPES } from '~/src/components/PageLayout/Modals';
import { LAYOUT_SLIDEIN_TYPES } from '~/src/components/PageLayout/SlideIns';
import { LAYOUT_TOAST_TYPES } from '~/src/components/PageLayout/Toasts';
import { useLayoutContext } from '../contexts/Layout';
import { history } from '../utils/history';
import analyticsService from '~/src/services/analytics';

const useDocumentsActions = (isDrafting) => {
  const {
    deleteDocument,
    deleteDocumentFile,
    updateDocument,
    updateDocumentMetadata,
    deleteDocumentFromMatters,
    getProject,
    fetchProject,
    duplicateDocument,
    projects,
  } = useMst((store) => ({
    documents: store.documents,
    deleteDocumentFromMatters: store.matter.deleteDocument,
    deleteDocument: store.documents.deleteDocument,
    deleteDocumentFile: store.documents.deleteDocumentFile,
    updateDocument: store.documents.updateDocument,
    updateDocumentMetadata: store.documents.updateDocumentMetadata,
    getProject: store.projects.getProject,
    fetchProject: store.projects.fetchProjectData,
    duplicateDocument: store.projects.duplicateDocument,
    projects: store.projects,
  }));

  const { showSlideIn, showToast, showModal, hideModal } = useLayoutContext();

  const getOrFetchDocument = async (document) => {
    let project = getProject(document.id);

    if (!project) {
      project = await fetchProject(document.id, document.title);
    }

    return project;
  };

  /* Actions */
  const handleDeleteDocument = (document, isDetailDocument = false) => {
    const handleDeleteConfirm = async () => {
      if (!isDetailDocument) await deleteDocumentFromMatters(document.id);
      const deleteFunc = isDetailDocument ? deleteDocumentFile : deleteDocument;
      const success = await deleteFunc(document.id);

      if (success) {
        if (isDrafting) {
          // isDrafting check is to make sure we are on drafting page before we do a project refresh

          // refreshProject connects to the backend and gets the latest copy of the project to update the store
          const refreshSuccess = await fetchProject(
            projects.curProjectId,
            true,
          );

          if (!refreshSuccess) {
            console.error('Refresh after Delete Failed');
          }
        }
        showToast(LAYOUT_TOAST_TYPES.success, {
          message: `Document "${document.title}" deleted.`,
        });
      } else {
        showToast(LAYOUT_TOAST_TYPES.error);
      }
      hideModal();
      return document;
    };

    showModal(LAYOUT_MODAL_TYPES.confirm, {
      title: `Delete Document "${document.title}"?`,
      message: `Are you sure you want to permanently delete this document? This action cannot be undone.`,
      onConfirm: handleDeleteConfirm,
      primaryActionTitle: 'Delete',
      processingText: 'Deleting...',
    });
  };

  const handleDownloadDocument = async (document) => {
    const handleDownload = async (showHighlights) => {
      const project = await getOrFetchDocument(document);
      const success = project.exportDoc(showHighlights);

      if (success) {
        showToast(LAYOUT_TOAST_TYPES.success, {
          message: `Document "${document.title}" downloaded.`,
        });
      } else {
        showToast(LAYOUT_TOAST_TYPES.error);
      }

      hideModal();
    };

    if (document.processedDocx) {
      // Document was generated after highlight toggle was implemented
      showModal(LAYOUT_MODAL_TYPES.downloadDocument, {
        documentTitle: document.title,
        onConfirm: handleDownload,
      });
    } else {
      // Document only has raw populated version, no need for prompt
      handleDownload(true);
    }
  };

  const handleEditDocument = async (document) => {
    const project = await getOrFetchDocument(document);

    if (project) {
      analyticsService.track('Document Edited', {
        projectId: project.id,
        documentId: document.id,
      });
      history.push(`/populate/${project.id}`);
    }
  };

  const handleViewDocument = async (document) => {
    const project = await getOrFetchDocument(document);
    if (project) {
      showSlideIn(LAYOUT_SLIDEIN_TYPES.projectPreview, {
        largeSlideIn: true,
        projectId: project.id,
      });
    }
  };

  const handleRenameDocument = async (document, isDetailDocument = false) => {
    const handleRenameConfirm = async (form) => {
      const { value } = form.fields.name;
      const updateFunc = isDetailDocument
        ? updateDocumentMetadata
        : updateDocument;
      const success = await updateFunc(document.id, { title: value });

      if (success) {
        showToast(LAYOUT_TOAST_TYPES.success, {
          message: `Document "${value}" renamed successfully.`,
        });
      } else {
        showToast(LAYOUT_TOAST_TYPES.error);
      }

      hideModal();
    };

    const dictionary_of_all_document_names = {};

    if (isDrafting) {
      // console.log(getProject(projects.curProjectId));
      getProject(projects.curProjectId).documents.forEach(
        (documentAlreadyInProject) => {
          if (documentAlreadyInProject.title != document.title) {
            if (
              documentAlreadyInProject.title in dictionary_of_all_document_names
            ) {
              dictionary_of_all_document_names[
                documentAlreadyInProject.title
              ] += 1;
            } else {
              dictionary_of_all_document_names[
                documentAlreadyInProject.title
              ] = 1;
            }
          }
        },
      );
    }

    showModal(LAYOUT_MODAL_TYPES.formField, {
      title: 'Rename Document',
      primaryActionTitle: 'Rename',
      fields: [
        {
          label: 'Name',
          id: 'name',
          type: 'rename',
          defaultValue: document.title,
          validation: VALIDATION_TYPES.renameDocument,
          documentNamesObject: dictionary_of_all_document_names,
        },
      ],
      onConfirm: handleRenameConfirm,
    });
  };

  const handleDuplicateDocument = async (document) => {
    const handleDuplicateConfirm = async (form) => {
      const success_await = await duplicateDocument(
        projects.curProjectId,
        document.id,
        form.fields?.number_of_documents_to_duplicate.value,
      );

      if (success_await) {
        showToast(LAYOUT_TOAST_TYPES.success, {
          message: `Document duplicated successfully`,
        });
      } else {
        showToast(LAYOUT_TOAST_TYPES.error);
      }

      hideModal();
    };

    showModal(LAYOUT_MODAL_TYPES.formField, {
      title: 'Duplicate Document',
      primaryActionTitle: 'Duplicate',
      analyticsName: 'Document Duplicated',
      fields: [
        {
          id: 'duplicate_modal_name',
          type: 'staticText',
          defaultValue: '',
          label: document.title,
        },
        {
          label: 'Number of Copies',
          id: 'number_of_documents_to_duplicate',
          type: 'duplicate_document',
          defaultValue: '1',
          validation:
            VALIDATION_TYPES.numeric &&
            VALIDATION_TYPES.duplicate_document_limit,
        },
      ],
      onConfirm: handleDuplicateConfirm,
    });
  };

  return {
    deleteDocument: handleDeleteDocument,
    editDocument: handleEditDocument,
    viewDocument: handleViewDocument,
    renameDocument: handleRenameDocument,
    downloadDocument: handleDownloadDocument,
    duplicateDocument: handleDuplicateDocument,
  };
};

export default useDocumentsActions;
